import { sendGTMEvent } from '@next/third-parties/google';
import { event_select_promotion } from '.';
import { priceFormatedObject, validatePrice } from '../category';
import { SHIPPING_INFO_MODALS_NAMES } from '@/constants';
import { findObjectByIdV2 } from '../global';
import { getPageType } from '@/constants/page-types.constants';
import { storePromotionAttribution } from './promotion-attribution';
import { getPromotionDestination } from './promotions';

const { setCookie } = require('cookies-next');

// `promotion` is already in the spec shape (see `buildPromotion`), so the
// impression and the click report identical identifiers. `href` is the resolved
// route the link navigates to, which becomes the absolute
// `promotion_destination`.
export const fireSelectPromotion = (promotion, href, locale) => {
  if (!promotion) return;

  // Cookies kept for the existing GTM setup; localStorage with a 24-hour TTL is
  // what the spec asks for, and what item-level attribution reads back.
  const destination = getPromotionDestination(href, locale);

  setCookie('promotion_id', promotion?.promotion_id);
  if (promotion?.promotion_name)
    setCookie('promotion_name', promotion?.promotion_name);
  storePromotionAttribution(promotion, destination);

  return event_select_promotion(promotion, destination);
};

function getShelfPrice(item, fromSuccess = false) {
  const result =
    item?.original_price ||
    item?.extension_attributes?.original_price ||
    item?.prices_with_tax?.original_price;
  if (fromSuccess) {
    return result + result * 0.15;
  }
  return result;
}
function getPrice(item) {
  return (
    item?.price_incl_tax ||
    item?.prices_with_tax?.final_price ||
    item?.prices_with_tax?.price
  );
}

export function getTotalDiscount(products, fromSuccess) {
  return products?.reduce((curr, item) => {
    return curr + getShelfPrice(item, fromSuccess) - getPrice(item);
  }, 0);
}

export function getTotalPrices(products) {
  return products?.reduce(
    (price, cur) =>
      (cur?.prices_with_tax?.price || cur?.price_incl_tax) * (cur?.qty || 1) +
      price,
    0
  );
}

const toISO8601 = (value) => {
  if (!value) return undefined;
  const raw = typeof value === 'string' ? value.trim() : value;
  const hasZone = typeof raw === 'string' && /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const normalized =
    typeof raw === 'string' && !raw.includes('T')
      ? raw.replace(' ', 'T') + (hasZone ? '' : 'Z')
      : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Date(date.getTime() + 3 * 60 * 60 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, '+03:00');
};

const getPhoneE164 = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 9) return undefined;
  return `966${digits.slice(-9)}`;
};

const toAbsoluteUrl = (url) => {
  if (!url) return undefined;
  if (/^https?:\/\//.test(url)) return url;
  if (typeof window === 'object')
    return new URL(url, window.location.origin).href;
  return undefined;
};

// `error` is the old string message or the new `{ error_code, errormsg |
// message }` object — see `utils/api-error.js`.
export const getFailedStatus = (error, fallback) => {
  const raw =
    typeof error === 'string'
      ? error
      : error?.message || error?.errormsg || error?.error;
  const reason = typeof raw === 'string' && raw.trim() ? raw.trim() : fallback;
  return `Failed: ${reason}`;
};

export function getGlobalVariables({
  pathname,
  locale,
  session,
  shippingMethod,
  shippingAddress,
}) {
  const user = session?.user;
  const full_name = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const phone = getPhoneE164(user?.mobilenumber);
  const email = user?.email?.trim()?.toLowerCase();
  const city = user
    ? shippingAddress?.city || shippingAddress?.region?.region
    : undefined;
  const country =
    user && shippingAddress?.country_id === 'SA' ? 'Saudi Arabia' : undefined;
  const shipping_method =
    shippingMethod === SHIPPING_INFO_MODALS_NAMES.HOME_DELIVERY
      ? 'Home Delivery'
      : shippingMethod === SHIPPING_INFO_MODALS_NAMES.STORE_PICKUP
        ? 'Store Pickup'
        : undefined;

  return {
    event: 'global_variables',
    store_language: locale === 'en' ? 'EN' : 'AR',
    ...(user?.id && { user_id: String(user?.id) }),
    page_type: getPageType(pathname),
    event_source: 'Website',
    login_status: user ? 'Logged In' : 'Guest',
    ...(shipping_method && { shipping_method }),
    ...(country && { country }),
    ...(city && { city }),
    ...(user && full_name && { full_name }),
    ...(user && phone && { phone }),
    ...(user && email && { email }),
  };
}

export function getCategoryClickPayload({
  menuTreeData,
  link,
  category,
  click_location,
}) {
  const slug = String(link || '')
    .split('?')[0]
    .split('/')
    .filter(Boolean)
    .at(-1);
  const clicked =
    category?.id || category?.category_id
      ? category
      : slug && findObjectByIdV2(menuTreeData, 'innova_url', slug);

  const clickedId = clicked?.id ?? clicked?.category_id;
  if (!clickedId) return null;

  const parent = clicked?.parent_id
    ? findObjectByIdV2(menuTreeData, 'id', clicked?.parent_id)
    : null;
  const level1 = parent || clicked;
  const level2 = parent ? clicked : null;

  return {
    category_id: String(level1?.id ?? level1?.category_id),
    category_name: level1?.name,
    ...(level2 && {
      category2_id: String(level2?.id ?? level2?.category_id),
      category2_name: level2?.name,
    }),
    click_location,
  };
}
export const getCallbackReferralUrl = () => {
  if (typeof window !== 'object') return undefined;

  let loginRedirect = null;
  try {
    loginRedirect = sessionStorage.getItem('loginRedirect');
  } catch {
    loginRedirect = null;
  }

  const callbackUrl = new URLSearchParams(window.location.search).get(
    'callbackUrl'
  );
  return toAbsoluteUrl(loginRedirect || callbackUrl || '/');
};

export const getItemListId = () => {
  if (typeof window !== 'object') return undefined;
  const path =
    window.location.pathname.replace(/^\/en(?=\/|$)/, '').split('?')[0] || '/';
  const segments = path.split('/').filter(Boolean);

  if (!segments.length) return 'home';
  if (segments[0] === 'search') return 'search';
  if (segments[0] === 'c' || segments[0] === 'b') {
    return `${segments[0]}_${segments.at(-1)}`;
  }
  return segments.at(-1);
};

export const getErrorContext = () => {
  if (typeof window !== 'object') return {};
  const path = window.location.pathname.replace(/^\/en(?=\/|$)/, '') || '/';
  return {
    error_location: window.location.href,
    page_name: document.title,
    page_type: getPageType(path),
  };
};

export const getAuthPayload = ({ user, method, status, referral_url }) => {
  const full_name = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const phone = getPhoneE164(user?.mobilenumber);
  const account_creation_date = toISO8601(user?.created_at);
  const referral = toAbsoluteUrl(referral_url) || getCallbackReferralUrl();

  return {
    method,
    status,
    ...(user?.id && { user_id: String(user?.id) }),
    ...(phone && { phone }),
    ...(user?.email && { email: user?.email }),
    ...(full_name && { full_name }),
    ...(referral && { referral_url: referral }),
    event_source: 'Website',
    ...(account_creation_date && { account_creation_date }),
  };
};

export const getPurchaseStats = (user) => {
  const { total_purchases, total_revenue, last_purchase_date } =
    user?.extension_attributes || {};
  const lastPurchase = toISO8601(last_purchase_date);
  return {
    ...(total_purchases != null && {
      total_purchases: Number(total_purchases),
    }),
    ...(total_revenue != null && { total_revenue: Number(total_revenue) }),
    ...(lastPurchase && { last_purchase_date: lastPurchase }),
  };
};

const PUSHED_PURCHASES_KEY = 'gtm_pushed_purchases';

export const wasPurchasePushed = (transaction_id) => {
  if (typeof window !== 'object' || !transaction_id) return false;
  try {
    const ids = JSON.parse(
      window.localStorage.getItem(PUSHED_PURCHASES_KEY) || '[]'
    );
    return ids.includes(String(transaction_id));
  } catch {
    return false;
  }
};

export const markPurchasePushed = (transaction_id) => {
  if (typeof window !== 'object' || !transaction_id) return;
  try {
    const ids = JSON.parse(
      window.localStorage.getItem(PUSHED_PURCHASES_KEY) || '[]'
    );
    window.localStorage.setItem(
      PUSHED_PURCHASES_KEY,
      JSON.stringify([...ids, String(transaction_id)].slice(-20))
    );
  } catch {
    return;
  }
};

export const getShippingTier = (method) => {
  const value = String(method || '').toLowerCase();
  if (!value) return undefined;
  return value.includes('pickup') ? 'Store Pickup' : 'Home Delivery';
};

const PAYMENT_TYPES = [
  ['moyasar', 'Credit Card'],
  ['creditcard', 'Credit Card'],
  ['mada', 'Mada'],
  ['applepay', 'Apple Pay'],
  ['stcpay', 'STC Pay'],
  ['tabby', 'Tabby'],
  ['tamara', 'Tamara'],
  ['madfu', 'Madfu'],
  ['mispay', 'MisPay'],
  ['emkan', 'Emkan'],
  ['paytabs', 'PayTabs'],
  ['cashondelivery', 'Cash On Delivery'],
  ['free', 'Free'],
];

// DAAM v3: English labels instead of raw gateway codes. An unknown code is
// passed through rather than dropped.
export const getPaymentType = (code) => {
  const value = String(code || '').toLowerCase();
  if (!value) return undefined;
  return PAYMENT_TYPES.find(([key]) => value.includes(key))?.[1] || code;
};

export function getBasicPromotions(promotion) {
  return {
    creative_name: promotion?.promotionImage,
    creative_slot: promotion?.key,
    location_id: promotion?.position,
    promotion_id: promotion?.id ?? promotion?.name ?? 'unknown',
    promotion_name: promotion?.name,
  };
}

// Set to true to log all GTM events to console for debugging

const buildGtmPayload = (event, data, user, ecommerce, allow_page_path) => {
  const event_data = { ...data, ...(user && { ...user }) };
  return {
    event: event,

    ...(ecommerce
      ? {
          ecommerce: {
            ...(allow_page_path && {
              page_path: window?.location?.pathname || '',
            }),
            ...event_data,
          },
        }
      : event_data),
  };
};

const pushGtmPayload = (payload, ecommerce) => {
  // Spec §2: the ecommerce object is cleared *before* the push, so values
  // never bleed from one ecommerce event into the next.
  if (ecommerce) sendGTMEvent({ ecommerce: null });
  sendGTMEvent(payload);
};

export const gtmEvent = (
  event,
  data,
  user,
  ecommerce = true,
  allow_page_path = true
) => {
  if (typeof window === 'object') {
    const payload = buildGtmPayload(
      event,
      data,
      user,
      ecommerce,
      allow_page_path
    );

    setTimeout(() => pushGtmPayload(payload, ecommerce), 500);
  }
};

// Same payload, pushed synchronously. Use this where the click may navigate away
// — a new tab, or an external banner — before a deferred push would run. The
// spec requires the event to be pushed *before* the navigation.
export const gtmEventNow = (
  event,
  data,
  user,
  ecommerce = true,
  allow_page_path = true
) => {
  if (typeof window === 'object') {
    pushGtmPayload(
      buildGtmPayload(event, data, user, ecommerce, allow_page_path),
      ecommerce
    );
  }
};

export const getUserIfno = (user) => {
  return !user
    ? {}
    : {
        ...(user?.firstname ? { firstname: user?.firstname } : {}),
        ...(user?.lastname ? { lastname: user?.lastname } : {}),
        ...(user?.id ? { user_id: String(user?.id) } : {}),
        ...(user?.email ? { user_email: user?.email } : {}),
        ...(user?.mobilenumber ? { user_phone: user?.mobilenumber } : {}),
      };
};

export const get_product_category_sub_category = ({ product }) => {
  const product_category =
    product?._category_?.main_category ||
    product?.extension_attributes?.category_names?.[0];
  const product_sub_category =
    product?._category_?.sub_categories?.at(0) ||
    product?.extension_attributes?.category_names?.[1];
  return {
    ...(product_category && {
      parent_category_id: product_category?.category_id || product_category?.id,
      parent_category_name: product_category?.name,
    }),
    ...(product_sub_category && {
      sub_category_id:
        product_sub_category?.category_id || product_sub_category?.id,
      sub_category_name: product_sub_category?.name,
    }),
  };
};

export const get_product_brand = ({ product }) => {
  const brand =
    product?.brand_label ||
    product?.option_text_brands?.[0] ||
    product?.extension_attributes?.brand_name;
  return {
    ...(brand && { brand }),
  };
};

export const get_product_sku = ({ product }) => {
  const sku = product?.extension_attributes?.product_sku || product?.sku;
  return {
    sku: Array.isArray(sku) ? sku?.at(0) : sku,
  };
};

export const get_product_price = ({ product, is_MRP = false }) => {
  const price = product?.prices_with_tax
    ? validatePrice(product?.prices_with_tax)
    : validatePrice(priceFormatedObject(product));
  const is_stock =
    (product?.stock?.is_in_stock && product?.stock?.qty > 0) || true;
  return {
    listed_price: !is_stock
      ? price?.originalPrice
      : price?.specialPrice || price?.originalPrice,
    ...(price?.specialPrice &&
      is_stock && {
        listed_discount: price?.discount || price?.discountPercentage,
        ...(is_MRP && { MRP: price?.originalPrice }),
      }),
  };
};

export const get_product_image = ({ product }) => {
  return {
    image:
      product?._media_?.image?.at(0)?.image ||
      (product?.extension_attributes?.product_image &&
        `${process.env.NEXT_PUBLIC_IMG_BASEURL}/media/catalog/product/${product?.extension_attributes?.product_image}`) ||
      product?.image_url ||
      `${process.env.NEXT_PUBLIC_IMG_BASEURL}/media/catalog/product/${product?.image}`,
  };
};
